import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Trash2 } from 'lucide-react';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const mono = "font-['JetBrains_Mono'] text-sm px-1 h-8";

export const InvoiceLinesTable = ({ lines, onChange, readOnly }) => {
  const update = (i, field, value) => onChange(lines.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const remove = (i) => onChange(lines.filter((_, idx) => idx !== i));

  if (!lines.length) {
    return <div data-testid="invoice-lines-empty" className="text-center py-8 text-muted-foreground text-sm">Nessuna riga. Aggiungi prodotti o una riga libera.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs uppercase h-8 px-2 w-[36px]">#</TableHead>
            <TableHead className="text-xs uppercase h-8 px-2 w-[90px]">Codice</TableHead>
            <TableHead className="text-xs uppercase h-8 px-2">Descrizione</TableHead>
            <TableHead className="text-xs uppercase h-8 px-1 w-[85px] text-center">Q.tà</TableHead>
            <TableHead className="text-xs uppercase h-8 px-1 w-[75px]">U.M.</TableHead>
            <TableHead className="text-xs uppercase h-8 px-1 w-[100px] text-right">Prezzo IVA incl.</TableHead>
            <TableHead className="text-xs uppercase h-8 px-1 w-[70px] text-right">IVA %</TableHead>
            <TableHead className="text-xs uppercase h-8 px-2 w-[100px] text-right">Totale</TableHead>
            <TableHead className="h-8 px-1 w-[32px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((l, i) => (
            <TableRow key={i} data-testid={`invoice-line-${i}`}>
              <TableCell className="py-1 px-2 text-xs text-muted-foreground">{i + 1}</TableCell>
              <TableCell className="py-1 px-1">
                <Input data-testid={`line-code-${i}`} value={l.product_code || ''} onChange={(e) => update(i, 'product_code', e.target.value)} className={mono} disabled={readOnly} />
              </TableCell>
              <TableCell className="py-1 px-1">
                <Input data-testid={`line-description-${i}`} value={l.description} onChange={(e) => update(i, 'description', e.target.value)} className="h-8 text-sm" disabled={readOnly} />
              </TableCell>
              <TableCell className="py-1 px-1">
                <Input data-testid={`line-quantity-${i}`} type="number" step="any" min="0" value={l.quantity} onChange={(e) => update(i, 'quantity', e.target.value)} className={`${mono} text-center`} disabled={readOnly} />
              </TableCell>
              <TableCell className="py-1 px-1">
                <Select value={l.unit || 'PZ'} onValueChange={(v) => update(i, 'unit', v)} disabled={readOnly}>
                  <SelectTrigger data-testid={`line-unit-${i}`} className="h-8 text-xs px-2"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="PZ">PZ</SelectItem><SelectItem value="KG">KG</SelectItem></SelectContent>
                </Select>
              </TableCell>
              <TableCell className="py-1 px-1">
                <Input data-testid={`line-price-${i}`} type="number" step="0.01" value={l.price} onChange={(e) => update(i, 'price', e.target.value)} className={`${mono} text-right font-semibold`} disabled={readOnly} />
              </TableCell>
              <TableCell className="py-1 px-1">
                <Input data-testid={`line-iva-${i}`} type="number" step="any" min="0" value={l.iva} onChange={(e) => update(i, 'iva', e.target.value)} className={`${mono} text-right`} disabled={readOnly} />
              </TableCell>
              <TableCell className="py-1 px-2 text-right font-['JetBrains_Mono'] text-sm font-semibold">
                {eur((parseFloat(l.quantity) || 0) * (parseFloat(l.price) || 0))}
              </TableCell>
              <TableCell className="py-1 px-1">
                {!readOnly && (
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(i)} data-testid={`remove-line-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// Scorporo IVA come sul server: per aliquota imponibile = lordo / (1 + aliquota)
export const computeInvoiceTotals = (lines) => {
  const groups = {};
  lines.forEach((l) => {
    const a = parseFloat(l.iva) || 0;
    groups[a] = (groups[a] || 0) + Math.round((parseFloat(l.quantity) || 0) * (parseFloat(l.price) || 0) * 100) / 100;
  });
  let imponibile = 0; let totale = 0;
  Object.entries(groups).forEach(([a, g]) => {
    imponibile += Math.round((g / (1 + parseFloat(a) / 100)) * 100) / 100;
    totale += g;
  });
  return { imponibile, imposta: totale - imponibile, totale };
};

export default InvoiceLinesTable;
